/*
 * Copyright (c) 2024-2025. Haodong JU
 */

import { VSelect, VTextarea, VTextField } from 'vuetify/lib/components'
import EnhancedColorPicker from '@/model/baseForm/fields/EnhancedColorPicker.vue'
import EnhancedImageUploader from '@/model/baseForm/fields/EnhancedImageUploader.vue'
import { createDisplayInfoFormFields } from '@/model/baseForm/membershipBenefitFormSchema'
import {
  createEntitySelectorSchema,
  createProductSelectorSchema
} from '@/model/baseForm/fields/EntitySelector/entitySelectorUtils'
import dayjs from 'dayjs'
import { showErrorMessage, showSuccessMessage } from '@/model/utils/utils'
import GlobalConfig from '@/model/global/GlobalSettings'
import DateSelector from '@/views/AadenPages/settings/reserveSetting/openTime/DateSelector.vue'
import { assetType } from '@/model/dataLayer/api/MembershipSystem/Asset'

/**
 * Creates a form schema for asset creation and editing
 *
 * @param {Object} options - Configuration options
 * @param {Array} options.assetType - Array of asset types
 * @param {Array} options.allDishes - Array of dishes for product selection
 * @returns {Array} - Array of form field objects
 */
export function createAssetFormSchema (options = {}) {
  // No need to destructure options as we'll use static values

  return [
    // Display Info Section
    ...createDisplayInfoFormFields({
      nameHint: '资产名称',
      descriptionHint: '资产描述',
      colorHint: '资产颜色',
      imageHint: '资产图片',
      nameRequired: true,
      descriptionRequired: false,
      colorRequired: false,
      imageRequired: false,
      tag: 'basic'
    }),
    // Asset Type Selection
    {
      key: 'assetType',
      name: '奖励类型',
      component: VSelect,
      tag: 'basic',
      required: true,
      componentProps: {
        items: assetType,
        itemText: 'name',
        itemValue: 'value'
      }
    },
    // Unlimited Supply Setting
    {
      key: 'unlimitedSupply',
      name: '无限供应',
      component: VSelect,
      tag: 'inventory',
      required: false,
      default: true,
      componentProps: {
        items: [
          { text: '是', value: true },
          { text: '否', value: false }
        ]
      }
    },
    // Single Inventory Setting
    {
      key: 'initialStock',
      name: '库存',
      component: VTextField,
      tag: 'inventory',
      required: true,
      componentProps: {
        type: 'number'
      },
      preconditions: { unlimitedSupply: false }
    },
    // Voucher Specific Settings
    {
      key: 'maxLimit',
      name: '最大限制',
      component: VTextField,
      tag: 'voucher',
      required: false,
      componentProps: {
        type: 'number'
      },
      preconditions: { assetType: 'VOUCHER' }
    },
    {
      key: 'usageMinAmount',
      name: '最低使用金额',
      component: VTextField,
      tag: 'voucher',
      required: false,
      componentProps: {
        type: 'number'
      },
      preconditions: { assetType: 'VOUCHER' }
    },
    {
      key: 'maxDiscountAmount',
      name: '最大折扣金额',
      component: VTextField,
      tag: 'voucher',
      required: false,
      componentProps: {
        type: 'number'
      },
      preconditions: { assetType: 'VOUCHER' }
    },
    {
      key: 'discountStr',
      name: '折扣值',
      component: VTextField,
      tag: 'voucher',
      required: false,
      preconditions: { assetType: 'VOUCHER' }
    },
    {
      key: 'isDiscountLimitedToHighestValueItem',
      name: '限制折扣应用于最高价值商品',
      component: VSelect,
      tag: 'voucher',
      required: false,
      default: false,
      componentProps: {
        items: [
          { text: '是', value: true },
          { text: '否', value: false }
        ]
      },
      preconditions: { assetType: 'VOUCHER' }
    },
    createProductSelectorSchema({
      key: 'productCodes',
      name: '适用产品',
      hint: '选择适用产品',
      required: false,
      multiple: true,
      tag: 'voucher',
      preconditions: { assetType: 'VOUCHER' }
    }),
    // Point Exchange Settings
    {
      key: 'point',
      name: '积分',
      component: VTextField,
      tag: 'point',
      required: false,
      componentProps: {
        type: 'number'
      },
      preconditions: { assetType: 'POINT_EXCHANGE' }
    },
    // Date Settings
    {
      key: 'activeSince',
      name: '生效日期',
      component: DateSelector,
      tag: 'dates',
      required: true,
      componentProps: {
        min: dayjs().format('YYYY-MM-DD')
      }
    },
    {
      key: 'expiredAt',
      name: '过期日期',
      component: DateSelector,
      tag: 'dates',
      required: true,
      componentProps: {
        min: dayjs().format('YYYY-MM-DD')
      }
    },
    // Advanced Settings
    {
      key: 'claimDaysLimit',
      name: '领取天数限制',
      component: VTextField,
      tag: 'advanced',
      required: false,
      componentProps: {
        type: 'number'
      }
    },
    {
      key: 'redemptionCodePrefix',
      name: '兑换码前缀',
      component: VTextField,
      tag: 'advanced',
      required: false,
      preconditions: { supportsRedemptionCode: true }
    },
    {
      key: 'active',
      name: '是否激活',
      component: VSelect,
      tag: 'advanced',
      required: false,
      default: false,
      componentProps: {
        items: [
          { text: '是', value: true },
          { text: '否', value: false }
        ]
      }
    },
    {
      key: 'supportsRedemptionCode',
      name: '支持兑换码',
      component: VSelect,
      tag: 'advanced',
      required: false,
      default: false,
      componentProps: {
        items: [
          { text: '是', value: true },
          { text: '否', value: false }
        ]
      }
    }
  ]
}

/**
 * Creates a schema for an asset selector with the ability to add new assets
 *
 * @param {Object} options - Configuration options
 * @param {string} options.key - The property key in the form data
 * @param {Function} options.loadFunction - Function to load the list of assets
 * @param {Function} options.saveFunction - Function to save a new asset
 * @param {Object} options.preconditions - Preconditions for showing the field
 * @returns {Object} - Schema object for BaseForm
 */
export function createAssetSelectorSchema (options) {
  const {
    key = 'trigger.assetId',
    name = '奖励',
    hint = '选择奖励资产',
    required = true,
    selectionOnly = false,
    loadFunction,
    saveFunction,
    preconditions = null
  } = options

  return createEntitySelectorSchema({
    key,
    name,
    hint,
    required,
    selectionOnly,
    loadFunction,
    addFunction: async (assetData) => {
      // Process the asset data
      const newAsset = {
        blId: assetData.blId || GlobalConfig.blId,
        displayInfo: {
          name: assetData.name,
          description: assetData.description || '',
          imageUrl: assetData.imageUrl || '',
          color: assetData.color || '#ffffff'
        },
        initialStock: assetData.initialStock || 0,
        currentStock: assetData.initialStock || 0,
        assetType: assetData.assetType,
        active: true,
        activeSince: dayjs().format('YYYY-MM-DD'),
        expiredAt: assetData.expiredAt || dayjs().add(1, 'year').format('YYYY-MM-DD')
      }

      // Set asset config based on asset type
      if (assetData.assetType === 'VOUCHER') {
        newAsset.assetConfig = JSON.stringify({
          type: 'VOUCHER',
          maxLimit: assetData.maxLimit || null,
          usageMinAmount: assetData.usageMinAmount || null,
          maxDiscountAmount: assetData.maxDiscountAmount || null,
          discountStr: assetData.discountStr || null,
          productCodes: assetData.productCodes || [],
          isDiscountLimitedToHighestValueItem: assetData.isDiscountLimitedToHighestValueItem || false
        })
      } else if (assetData.assetType === 'POINT_EXCHANGE') {
        newAsset.assetConfig = JSON.stringify({
          type: 'POINT_EXCHANGE',
          point: assetData.point || 0
        })
      } else if (assetData.assetType === 'PHYSICAL_REWARD') {
        newAsset.assetConfig = JSON.stringify({
          type: 'PHYSICAL_REWARD'
        })
      }

      try {
        // Save the asset using the provided save function
        const res = await saveFunction(newAsset)
        showSuccessMessage('奖励资产创建成功')
        return res
      } catch (error) {
        console.error('Error creating asset:', error)
        showErrorMessage('创建奖励资产失败: ' + (error.message || '未知错误'))
        throw error
      }
    },
    formSchemas: [
      {
        key: 'name',
        name: '资产名称',
        component: VTextField,
        required: true,
        hint: '输入资产名称'
      },
      {
        key: 'description',
        name: '资产描述',
        component: VTextarea,
        required: false,
        hint: '输入资产描述',
        fullWidth: true
      },
      {
        key: 'assetType',
        name: '资产类型',
        component: VSelect,
        required: true,
        hint: '选择资产类型',
        componentProps: {
          items: assetType,
          itemText: 'name',
          itemValue: 'value'
        }
      },
      {
        key: 'initialStock',
        name: '初始库存',
        component: VTextField,
        required: true,
        hint: '输入初始库存',
        componentProps: {
          type: 'number'
        }
      },
      {
        key: 'color',
        name: '颜色',
        component: EnhancedColorPicker,
        required: false,
        hint: '选择资产颜色'
      },
      {
        key: 'imageUrl',
        name: '图片',
        component: EnhancedImageUploader,
        required: false,
        hint: '上传资产图片'
      },
      // VOUCHER specific fields
      {
        key: 'maxLimit',
        name: '最大限制',
        component: VTextField,
        required: false,
        hint: '输入最大限制',
        preconditions: {
          assetType: 'VOUCHER'
        },
        componentProps: {
          type: 'number'
        }
      },
      {
        key: 'usageMinAmount',
        name: '最低使用金额',
        component: VTextField,
        required: false,
        hint: '输入最低使用金额',
        preconditions: {
          assetType: 'VOUCHER'
        },
        componentProps: {
          type: 'number'
        }
      },
      {
        key: 'maxDiscountAmount',
        name: '最大折扣金额',
        component: VTextField,
        required: false,
        hint: '输入最大折扣金额',
        preconditions: {
          assetType: 'VOUCHER'
        },
        componentProps: {
          type: 'number'
        }
      },
      {
        key: 'discountStr',
        name: '折扣值',
        component: VTextField,
        required: false,
        hint: '输入折扣值',
        preconditions: {
          assetType: 'VOUCHER'
        }
      },
      {
        key: 'isDiscountLimitedToHighestValueItem',
        name: '限制折扣应用于最高价值商品',
        component: VSelect,
        required: false,
        hint: '选择是否限制折扣应用于最高价值商品',
        default: false,
        componentProps: {
          items: [
            { text: '是', value: true },
            { text: '否', value: false }
          ]
        },
        preconditions: {
          assetType: 'VOUCHER'
        }
      },
      createProductSelectorSchema({
        key: 'productCodes',
        name: '适用产品',
        hint: '选择适用产品',
        required: false,
        multiple: true,
        preconditions: {
          assetType: 'VOUCHER'
        }
      }),
      // POINT_EXCHANGE specific fields
      {
        key: 'point',
        name: '积分',
        component: VTextField,
        required: false,
        hint: '输入积分',
        preconditions: {
          assetType: 'POINT_EXCHANGE'
        },
        componentProps: {
          type: 'number'
        }
      }
    ],
    dialogTitle: '添加奖励资产',
    saveBtnName: '保存',
    preconditions
  })
}
